/**
 * The Library's Slides tab, as ONE object.
 *
 * Written to the same shape as `routes/dashboard/deckFilterState.ts`, and
 * deliberately so: the two pages now carry the same chrome — a title row, a
 * full-width search, a row of filter pills, a chip row — and the thing that
 * keeps them the same is that the state underneath is the same kind of thing.
 * Parsing and serialising are pure and total in both directions, so a filtered
 * library can be copied, bookmarked, reloaded and sent to someone else.
 *
 * TWO RULES CARRIED OVER FROM THE DECKS CODEC, for the same reasons:
 * a value equal to its default is DELETED rather than written, so an
 * unfiltered library has a clean address bar; and junk in a param falls back
 * to the default instead of throwing, because a bad link should be a wrong
 * view rather than a blank page.
 *
 * ONE RULE THAT IS THIS PAGE'S OWN: the category axis is `cat`, never
 * `category`. `?category=` belongs to the BANK tab — `components/bank/
 * BankLibrary.tsx` hydrates and mirrors it on `/library/bank`, and two
 * components writing one param would fight. The old name is still READ here,
 * because `onCopyFilterLink` handed out `/library?category=…` links before any
 * of this existed; it normalises to `cat` on arrival and is never written back.
 */

/** Sentinel for the "Uncategorised" row. Library items always carry a category
 *  today, so the rail hides the row at count 0 — but the URL codec still has to
 *  know the sentinel, which is why it lives here rather than beside the
 *  derivations. `useLibraryFilters` re-exports it. */
export const UNCATEGORISED = ' uncategorised';

/** One slide, or a series. `null` is both. */
export type LibraryTypeFilter = null | 'single' | 'series';

/** Over the Run 8 usage registry: has any deck taken a copy of this item?
 *  `null` is any. */
export type LibraryUsedFilter = null | 'used' | 'never';

/** The rail's "Recently inserted" row, as a NARROWING axis rather than a sort.
 *  Run 4's lesson on the decks page, and the reason it is here: a row that
 *  shows a count must show exactly what it counts, and a row that only
 *  reordered the body claimed a number the body then contradicted. `null` is
 *  any time; `recent` is the window in `libraryFilterOptions.ts`. */
export type LibraryInsertedFilter = null | 'recent';

/** Whether the body is one grid or a heading per category. The grouped body
 *  itself is Run 10; the pill persists the choice from Run 9 so the two runs
 *  can land separately. */
export type LibraryGroupMode = 'category' | 'none';

/** How the items are ordered. `saved` is newest-first over `savedAt`,
 *  `inserted` over `lastInsertedAt`, `used` over the insert tally. */
export type LibrarySort = 'saved' | 'inserted' | 'used' | 'name';

export type LibraryFilterState = {
  query: string;
  type: LibraryTypeFilter;
  /** A category path, the UNCATEGORISED sentinel, or null for all. */
  category: string | null;
  used: LibraryUsedFilter;
  inserted: LibraryInsertedFilter;
  group: LibraryGroupMode;
  sort: LibrarySort;
};

export const DEFAULT_LIBRARY_FILTER_STATE: LibraryFilterState = {
  query: '',
  type: null,
  category: null,
  used: null,
  inserted: null,
  group: 'category',
  sort: 'saved',
};

/** Short because they end up in links people paste to each other; frozen
 *  because those links outlive the code that wrote them. */
export const LIBRARY_FILTER_PARAMS = {
  query: 'q',
  type: 'type',
  category: 'cat',
  used: 'used',
  inserted: 'ins',
  group: 'group',
  sort: 'sort',
} as const;

/** Read, never written — see the header. The BANK tab owns this name. */
const LEGACY_CATEGORY_PARAM = 'category';

/** The wire form of the Uncategorised bucket — the sentinel is unprintable. */
const UNCATEGORISED_PARAM = 'uncategorised';

const TYPES: readonly Exclude<LibraryTypeFilter, null>[] = ['single', 'series'];
const USED: readonly Exclude<LibraryUsedFilter, null>[] = ['used', 'never'];
const INSERTED: readonly Exclude<LibraryInsertedFilter, null>[] = ['recent'];
const GROUPS: readonly LibraryGroupMode[] = ['category', 'none'];
const SORTS: readonly LibrarySort[] = ['saved', 'inserted', 'used', 'name'];

function oneOf<T extends string>(raw: string | null, allowed: readonly T[], fallback: T | null): T | null {
  if (!raw) return fallback;
  return (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
}

/** Read the view out of a query string. */
export function parseLibraryFilterState(
  params: URLSearchParams,
  defaults: LibraryFilterState = DEFAULT_LIBRARY_FILTER_STATE,
): LibraryFilterState {
  const rawCat = params.get(LIBRARY_FILTER_PARAMS.category) ?? params.get(LEGACY_CATEGORY_PARAM);
  const cat = rawCat ? (rawCat === UNCATEGORISED_PARAM ? UNCATEGORISED : rawCat) : defaults.category;
  return {
    query: params.get(LIBRARY_FILTER_PARAMS.query) ?? defaults.query,
    type: oneOf(params.get(LIBRARY_FILTER_PARAMS.type), TYPES, defaults.type),
    category: cat,
    used: oneOf(params.get(LIBRARY_FILTER_PARAMS.used), USED, defaults.used),
    inserted: oneOf(params.get(LIBRARY_FILTER_PARAMS.inserted), INSERTED, defaults.inserted),
    group: oneOf(params.get(LIBRARY_FILTER_PARAMS.group), GROUPS, defaults.group) ?? DEFAULT_LIBRARY_FILTER_STATE.group,
    sort: oneOf(params.get(LIBRARY_FILTER_PARAMS.sort), SORTS, defaults.sort) ?? DEFAULT_LIBRARY_FILTER_STATE.sort,
  };
}

/**
 * Write the view back into a query string.
 *
 * `base` carries any params that aren't ours, so nothing else on the page
 * loses its own state when a filter changes.
 */
export function libraryFilterStateToParams(
  state: LibraryFilterState,
  base?: URLSearchParams,
  defaults: LibraryFilterState = DEFAULT_LIBRARY_FILTER_STATE,
): URLSearchParams {
  const next = new URLSearchParams(base);
  next.delete(LEGACY_CATEGORY_PARAM); // an old link normalises to `cat`
  const put = (key: string, value: string | null, isDefault: boolean) => {
    if (isDefault || !value) next.delete(key);
    else next.set(key, value);
  };
  put(LIBRARY_FILTER_PARAMS.query, state.query, state.query === defaults.query);
  put(LIBRARY_FILTER_PARAMS.type, state.type, state.type === defaults.type);
  put(
    LIBRARY_FILTER_PARAMS.category,
    state.category === UNCATEGORISED ? UNCATEGORISED_PARAM : state.category,
    state.category === defaults.category,
  );
  put(LIBRARY_FILTER_PARAMS.used, state.used, state.used === defaults.used);
  put(LIBRARY_FILTER_PARAMS.inserted, state.inserted, state.inserted === defaults.inserted);
  put(LIBRARY_FILTER_PARAMS.group, state.group, state.group === defaults.group);
  put(LIBRARY_FILTER_PARAMS.sort, state.sort, state.sort === defaults.sort);
  return next;
}

/** The axes that NARROW the item list. Group and sort describe how the same
 *  items are shown, so they are presentation, and the chip row must not offer
 *  to clear them. */
export const LIBRARY_FILTER_AXES = ['query', 'type', 'category', 'used', 'inserted'] as const;

export type LibraryFilterAxis = (typeof LIBRARY_FILTER_AXES)[number];

/** How many filters are on — what the chip row and the Clear control read, so
 *  neither has to list the axes itself. */
export function libraryActiveFilterCount(state: LibraryFilterState): number {
  return LIBRARY_FILTER_AXES.filter((axis) => state[axis] !== DEFAULT_LIBRARY_FILTER_STATE[axis]).length;
}

/** "Clear all": drop every filter, keep how the user likes things shown. */
export function clearedLibraryFilterState(state: LibraryFilterState): LibraryFilterState {
  return { ...state, query: '', type: null, category: null, used: null, inserted: null };
}
